<template>
    <div>
        <v-card class="sales-purchase-ledger" outlined>
            <v-card-title class="page-heading primary">
                <v-row align="center">
                    <v-col cols="12" sm="9">{{ $t('sales_purchase_ledger.heading') }}</v-col>
                    <v-col cols="12" sm="3" class="d-flex justify-end">
                        <v-btn
                            class="minw100 mr-2"
                            color="white"
                            outlined
                            @click="exportToFile"
                        >
                            <v-icon left>fal fa-download</v-icon>
                            {{ $t('general.export') }}
                        </v-btn>

                        <v-btn
                            class="minw100"
                            color="white"
                            outlined
                            @click="print"
                        >
                            <v-icon left>fal fa-print</v-icon>
                            {{ $t('general.print') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text>
                <sales-purchase-ledger-search
                    :filter.sync="filter"
                />

                <v-data-table
                    class="items-table striped"
                    :headers="headers"
                    :items="items"
                    :options.sync="pagination"
                    :footer-props="{ 'items-per-page-options': [20, 50, 100, 200] }"
                    :items-per-page.sync="pagination.itemsPerPage"
                    :server-items-length="itemsTotal"
                    :loading="loading"
                    :mobile-breakpoint="0"
                    dense
                >
                    <template v-slot:foot="{ items }">
                        <tfoot>
                            <tr>
                                <td></td>
                                <td></td>
                                <td>{{ $t('general.total').toUpperCase() }}:</td>
                                <td class="text-right">{{ summary.inputAmount }}</td>
                                <td class="text-right">{{ summary.outputAmount }}</td>
                                <td></td>
                                <td class="text-right">{{ summary.cashSalesAmount }}</td>
                            </tr>
                        </tfoot>
                    </template>
                    <template v-slot:item="{ item, index }">
                        <tr>
                            <td class="text-center">{{ index + 1 }}</td>
                            <td>{{ item.date | date }}</td>
                            <td>{{ item.document }}</td>
                            <td
                                class="font-weight-bold text-right"
                                :class="itemClasses(item).input"
                            >
                                {{ Money.parse(item.input_amount).toFormat('0.00') }}
                            </td>
                            <td
                                class="font-weight-bold text-right"
                                :class="itemClasses(item).output"
                            >
                                {{ Money.parse(item.output_amount).toFormat('0.00') }}
                            </td>
                            <td class="text-right">
                                <span v-if="item.meta.cash_sale_paid_at">
                                    {{ item.meta.cash_sale_paid_at | date }}
                                </span>
                            </td>
                            <td class="font-weight-bold text-right">
                                <span v-if="item.meta.cash_sale_amount">
                                    {{ Money.parse(item.meta.cash_sale_amount).toFormat('0.00') }}
                                </span>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import ApiService from '@/services/ApiService';
import SalesPurchaseLedgerSearch from '@/views/shared/admin/salesPurchaseLedger/SalesPurchaseLedgerSearch';
import HtmlGen from '@/wrappers/HtmlGen';

export default {
    components: {
        SalesPurchaseLedgerSearch,
    },

    data() {
        return {
            items: [],
            itemsTotal: 0,
            loading: false,
            summary: {
                inputAmount: '',
                outputAmount: '',
                cashSalesAmount: '',
            },

            filter: {
                search: null,
                startDate: moment().subtract(1, 'month').format('YYYY-MM-DD') + ' 00:00:00',
                endDate: moment().format('YYYY-MM-DD') + ' 23:59:59',
                productType: null,
            },

            headers: [
                { text: '', value: 'number' },
                { text: this.$t('general.date'), value: 'date' },
                { text: this.$t('sales_purchase_ledger.description'), value: 'description', sortable: false },
                { text: this.$t('sales_purchase_ledger.input_amount'), value: 'input_amount', sortable: false, align: 'right' },
                { text: this.$t('sales_purchase_ledger.output_amount'), value: 'output_amount', sortable: false, align: 'right' },
                { text: this.$t('sales_purchase_ledger.cash_sales_date'), value: 'meta.cash_sale_paid_at', sortable: false, align: 'right' },
                { text: this.$t('sales_purchase_ledger.cash_sales_amount'), value: 'meta.cash_sale_amount', sortable: false, align: 'right' },
            ],

            pagination: {
                sortBy: ['date'],
                itemsPerPage: 100,
                page: 1,
                sortDesc: [true],
            },
        };
    },

    watch: {
        pagination: {
            handler() {
                this.getItems();
            },
            deep: true
        },

        filter() {
            this.getItems();
        },
    },

    methods: {
        getItems() {
            const { sortBy, sortDesc, page, itemsPerPage } = this.pagination;
            const sortField = sortBy[0] ? sortBy[0] : 'date';
            const sortOrder = sortDesc[0] ? 'descending' : 'ascending';

            let url = `/admin/sales-purchase-ledger?page=${page}&itemsPerPage=${itemsPerPage}&sortField=${sortField}&sortOrder=${sortOrder}`;

            if (this.filter) {
                Object.keys(this.filter).forEach(key => {
                    if(this.filter[key]) {
                        url += `&${key}=${this.filter[key]}`;
                    }
                })
            }

            this.loading = true;

            ApiService.get(url).then(response => {
                this.loading = false;
                this.items = response.data.data.map(item => {
                    if(item.type === 'price_calculation') {
                        const clientAddress = [(item.client || {}).address, (item.client || {}).city].filter(Boolean).join(', ')
                        item.document = this.$t('sales_purchase_ledger.price_calculation', {
                            value: item.number,
                            value2: item.client?.name || '',
                            value3: clientAddress ? `, ${clientAddress}` : ''
                        })
                    } else if(item.type === 'price_leveling_auto') {
                        item.document = this.$t('sales_purchase_ledger.price_leveling_auto_' + (item.meta.price_calculation ? 'calc' : 'receipt'), {
                            value: item.meta.price_calculation?.number || item.meta.order?.number || ''
                        })
                    } else {
                        item.document = this.$t('sales_purchase_ledger.' + item.type, { value: item.number })
                    }

                    return item;
                });

                this.itemsTotal = response.data.total;

                this.summary = {
                    inputAmount: Money.parse(response.data.total_input_amount).toFormat('0.00'),
                    outputAmount: Money.parse(response.data.total_output_amount).toFormat('0.00'),
                    cashSalesAmount: Money.parse(response.data.total_cash_sales).toFormat('0.00'),
                }
            });
        },

        async exportToFile() {
            const response = await ApiService.get('/admin/sales-purchase-ledger/export', { 
                responseType: 'blob',
                params: { startDate: this.filter.startDate, endDate: this.filter.endDate },
            });
            const fileBlob = new Blob([response.data]);
            const href = window.URL.createObjectURL(fileBlob);
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('download', 'sales-purchase-ledger.xlsx');
            document.body.appendChild(link);
            link.click();
        },

        print() {
            const lines = [];

            lines.push(`[L]`);
            lines.push(`[L]${this.$t('general.company')}: ${this.tenant.venue.company_name} / ${this.tenant.venue.company_address}[R]Obrazac EM`);
            lines.push(`[L]${this.tenant.venue.name} / ${this.tenant.venue.address}`);

            lines.push(`[L]`);
            lines.push(`[L]`);
            lines.push(`[C][H22]${this.$t('sales_purchase_ledger.heading')}`);
            lines.push(`[C]${moment(this.filter.startDate).format('DD.MM.YYYY.')} - ${moment(this.filter.endDate).format('DD.MM.YYYY.')}`);
            lines.push(`[L]`);

            lines.push(`[HR]`);
            lines.push(
                `[L]` +
                `[L][H00]${this.$t('sales_purchase_ledger.date')}` +
                `[L]${this.$t('sales_purchase_ledger.description')}` +
                `[R]${this.$t('sales_purchase_ledger.input_amount')}` +
                `[R]${this.$t('sales_purchase_ledger.output_amount')}` +
                `[R]${this.$t('sales_purchase_ledger.cash_sales_date')}` +
                `[R]${this.$t('sales_purchase_ledger.cash_sales_amount')}`
            );
            lines.push(`[HR]`);

            this.items.forEach((item, index) => {
                lines.push(
                    `[L]${index + 1}` +
                    `[L][H00]${moment(item.date).format('DD.MM.YYYY.')}` +
                    `[L]${item.document}` +
                    `[R]${Money.parse(item.input_amount).toFormat('0.00')}` +
                    `[R]${Money.parse(item.output_amount).toFormat('0.00')}` +
                    `[R]${item.meta.cash_sale_paid_at ? moment(item.meta.cash_sale_paid_at).format('DD.MM.YYYY.') : ''}` +
                    `[R]${item.meta.cash_sale_paid_at ? Money.parse(item.meta.cash_sale_amount).toFormat('0.00') : ''}`
                );
            })

            lines.push(`[HR]`);

            lines.push(
                `[L]` +
                `[L][H00]` +
                `[L]${this.$t('general.total').toUpperCase()}:` +
                `[R]${this.summary.inputAmount}` +
                `[R]${this.summary.outputAmount}` +
                `[R]` +
                `[R]${this.summary.cashSalesAmount}`
            );

            const html = lines.map(line => HtmlGen.fromTextLine(line)).join('');
            this.$printService.printCustom(null, html, null, { choosePrinter: true })
        },

        itemClasses(item) {
            const inputAmount = parseFloat(item.input_amount);
            const outputAmount = parseFloat(item.output_amount);

            return {
                input: inputAmount === 0 ? '' : (inputAmount > 0 ? 'success--text' : 'error--text'),
                output: outputAmount === 0 ? '' : (outputAmount > 0 ? 'error--text' : 'success--text'),
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.sales-purchase-ledger {
    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }

    ::v-deep .items-table {
        margin-top: 28px;

        th {
            font-size: 11px;
            padding: 0 4px;
            max-width: 80px;

            span {
                white-space: break-spaces;
            }
        }

        td {
            font-size: 13px;
            padding: 0 4px;
        }

        .separator {
            border-left: 1px solid rgba(0, 0, 0, 0.5);
            padding-left: 12px;
        }

        tfoot {
            font-weight: 500;
            background: #f3f4fb;
        }
    }
}
</style>
